<template>
  <div class="m-guide">
    <!-- 1) Qué teléfono tiene -->
    <div v-if="!platform" class="m-guide__ask">
      <p class="m-guide__question">{{ $t("mobile.installWhichPhone") }}</p>
      <div class="m-guide__choices">
        <button type="button" class="m-guide__choice" @click="platform = 'android'">
          <i class="fab fa-android" aria-hidden="true"></i>
          <span>Android</span>
        </button>
        <button type="button" class="m-guide__choice" @click="platform = 'ios'">
          <i class="fab fa-apple" aria-hidden="true"></i>
          <span>iPhone</span>
        </button>
      </div>
    </div>

    <!-- 2) Pasos con un dibujo de cada pantalla -->
    <div v-else>
      <button type="button" class="m-guide__back" @click="platform = platform === 'ios' ? 'android' : 'ios'">
        <i class="fas fa-exchange-alt" aria-hidden="true"></i>
        {{ $t("mobile.installOtherPhone", { name: platform === "ios" ? "Android" : "iPhone" }) }}
      </button>

      <div v-if="platform === 'android' && canPrompt" class="m-guide__quick">
        <p>{{ $t("mobile.installAndroidQuick") }}</p>
        <button type="button" class="btn btn-primary m-guide__quick-btn" @click="$emit('prompt')">
          {{ $t("mobile.installNow") }}
        </button>
        <p class="m-guide__or">{{ $t("mobile.installOrManual") }}</p>
      </div>

      <ol class="m-guide__steps">
        <li v-for="(step, i) in steps" :key="step.key" class="m-guide__step">
          <div class="m-guide__step-text">
            <span class="m-guide__num">{{ i + 1 }}</span>
            <span>{{ step.text }}</span>
          </div>

          <!-- iPhone · Safari: botón Compartir en la barra de abajo -->
          <svg v-if="step.key === 'ios-share'" viewBox="0 0 240 120" class="m-guide__img" role="img" :aria-label="step.text">
            <!-- el teléfono sigue más abajo del dibujo: se ve solo la parte de arriba de la barra -->
            <rect class="ig-screen" x="20" y="4" width="200" height="140" rx="14" />
            <rect class="ig-line" x="40" y="20" width="160" height="8" rx="4" />
            <rect class="ig-line" x="40" y="36" width="120" height="8" rx="4" />
            <rect class="ig-bar" x="32" y="56" width="176" height="20" rx="10" />
            <text class="ig-small" x="120" y="69.5" text-anchor="middle">{{ host }}</text>
            <rect class="ig-bar" x="21" y="84" width="198" height="36" />
            <path class="ig-muted-stroke" d="M38 100 l6 -5 m-6 5 l6 5" />
            <path class="ig-muted-stroke" d="M84 100 l-6 -5 m6 5 l-6 5" />
            <circle class="ig-hl-bg" cx="120" cy="100" r="13" />
            <g class="ig-accent-stroke">
              <rect x="114" y="98" width="12" height="11" rx="2" />
              <path d="M120 103 v-12 m-4 4 l4 -4 l4 4" />
            </g>
            <circle class="ig-hl" cx="120" cy="100" r="13" />
            <path class="ig-muted-stroke" d="M156 95 h-8 a3 3 0 0 0 -3 3 v8 M150 95 v-4 h10 v10 h-4" />
            <rect class="ig-muted-stroke" x="188" y="94" width="11" height="11" rx="2" />
          </svg>

          <!-- iPhone: «Agregar a pantalla de inicio» en el menú de Compartir -->
          <svg v-else-if="step.key === 'ios-add'" viewBox="0 0 240 120" class="m-guide__img" role="img" :aria-label="step.text">
            <rect class="ig-screen" x="20" y="4" width="200" height="112" rx="14" />
            <rect class="ig-bar" x="30" y="14" width="180" height="96" rx="10" />
            <rect class="ig-line" x="44" y="26" width="90" height="7" rx="3.5" />
            <rect class="ig-line" x="44" y="46" width="110" height="7" rx="3.5" />
            <rect class="ig-hl-bg" x="36" y="62" width="168" height="24" rx="6" />
            <text class="ig-text" x="46" y="78">{{ $t("mobile.installIosAddLabel") }}</text>
            <g class="ig-accent-stroke">
              <rect x="180" y="67" width="14" height="14" rx="3" />
              <path d="M187 70.5 v7 m-3.5 -3.5 h7" />
            </g>
            <rect class="ig-hl" x="36" y="62" width="168" height="24" rx="6" />
            <rect class="ig-line" x="44" y="96" width="70" height="7" rx="3.5" />
          </svg>

          <!-- iPhone: «Agregar» arriba a la derecha -->
          <svg v-else-if="step.key === 'ios-confirm'" viewBox="0 0 240 120" class="m-guide__img" role="img" :aria-label="step.text">
            <rect class="ig-screen" x="20" y="4" width="200" height="112" rx="14" />
            <rect class="ig-bar" x="20" y="4" width="200" height="30" rx="14" />
            <text class="ig-small" x="34" y="23">{{ $t("mobile.installIosCancel") }}</text>
            <rect class="ig-hl-bg" x="164" y="10" width="48" height="18" rx="9" />
            <text class="ig-accent-text" x="188" y="23" text-anchor="middle">{{ $t("mobile.installIosConfirm") }}</text>
            <rect class="ig-hl" x="164" y="10" width="48" height="18" rx="9" />
            <image v-if="logo" :href="logo" x="40" y="48" width="36" height="36" class="ig-logo" />
            <rect v-else class="ig-accent" x="40" y="48" width="36" height="36" rx="8" />
            <rect class="ig-bar" x="88" y="56" width="112" height="20" rx="4" />
            <text class="ig-text" x="96" y="70">{{ name }}</text>
          </svg>

          <!-- Android · Chrome: los tres puntitos arriba a la derecha -->
          <svg v-else-if="step.key === 'android-menu'" viewBox="0 0 240 120" class="m-guide__img" role="img" :aria-label="step.text">
            <rect class="ig-screen" x="20" y="4" width="200" height="112" rx="14" />
            <rect class="ig-bar" x="20" y="4" width="200" height="34" rx="14" />
            <rect class="ig-screen" x="32" y="12" width="140" height="18" rx="9" />
            <text class="ig-small" x="44" y="24.5">{{ host }}</text>
            <circle class="ig-hl-bg" cx="198" cy="21" r="12" />
            <circle class="ig-accent" cx="198" cy="15" r="2" />
            <circle class="ig-accent" cx="198" cy="21" r="2" />
            <circle class="ig-accent" cx="198" cy="27" r="2" />
            <circle class="ig-hl" cx="198" cy="21" r="12" />
            <rect class="ig-line" x="40" y="54" width="160" height="8" rx="4" />
            <rect class="ig-line" x="40" y="70" width="120" height="8" rx="4" />
            <rect class="ig-line" x="40" y="86" width="140" height="8" rx="4" />
          </svg>

          <!-- Android: «Instalar app» en el menú -->
          <svg v-else-if="step.key === 'android-install'" viewBox="0 0 240 120" class="m-guide__img" role="img" :aria-label="step.text">
            <rect class="ig-screen" x="20" y="4" width="200" height="112" rx="14" />
            <rect class="ig-bar ig-shadow" x="92" y="10" width="120" height="100" rx="8" />
            <rect class="ig-line" x="104" y="22" width="70" height="7" rx="3.5" />
            <rect class="ig-line" x="104" y="40" width="84" height="7" rx="3.5" />
            <rect class="ig-hl-bg" x="96" y="54" width="112" height="22" rx="5" />
            <g class="ig-accent-stroke">
              <rect x="104" y="58" width="9" height="14" rx="2" />
              <path d="M108.5 61 v6 m-2.5 -2.5 l2.5 2.5 l2.5 -2.5" />
            </g>
            <text class="ig-text" x="119" y="69">{{ $t("mobile.installAndroidLabel") }}</text>
            <rect class="ig-hl" x="96" y="54" width="112" height="22" rx="5" />
            <rect class="ig-line" x="104" y="86" width="60" height="7" rx="3.5" />
          </svg>

          <!-- Android: confirmar «Instalar» -->
          <svg v-else-if="step.key === 'android-confirm'" viewBox="0 0 240 120" class="m-guide__img" role="img" :aria-label="step.text">
            <rect class="ig-screen" x="20" y="4" width="200" height="112" rx="14" />
            <rect class="ig-bar ig-shadow" x="40" y="18" width="160" height="84" rx="12" />
            <image v-if="logo" :href="logo" x="54" y="30" width="26" height="26" class="ig-logo" />
            <rect v-else class="ig-accent" x="54" y="30" width="26" height="26" rx="6" />
            <text class="ig-text" x="88" y="47">{{ name }}</text>
            <text class="ig-small" x="140" y="88" text-anchor="middle">{{ $t("mobile.installIosCancel") }}</text>
            <rect class="ig-hl-bg" x="158" y="76" width="36" height="18" rx="9" />
            <text class="ig-accent-text" x="176" y="88.5" text-anchor="middle">{{ $t("mobile.installButton") }}</text>
            <rect class="ig-hl" x="158" y="76" width="36" height="18" rx="9" />
          </svg>

          <!-- Los dos: abrir desde el ícono de la pantalla de inicio -->
          <svg v-else-if="step.key === 'open'" viewBox="0 0 240 120" class="m-guide__img" role="img" :aria-label="step.text">
            <rect class="ig-screen" x="20" y="4" width="200" height="112" rx="14" />
            <g v-for="n in 7" :key="n">
              <rect
                class="ig-line"
                :x="38 + ((n - 1) % 4) * 46"
                :y="18 + Math.floor((n - 1) / 4) * 48"
                width="28"
                height="28"
                rx="7"
              />
            </g>
            <image v-if="logo" :href="logo" x="176" y="66" width="28" height="28" class="ig-logo" />
            <rect v-else class="ig-accent" x="176" y="66" width="28" height="28" rx="7" />
            <rect class="ig-hl" x="172" y="62" width="36" height="36" rx="10" />
            <text class="ig-small" x="190" y="108" text-anchor="middle">{{ shortName }}</text>
          </svg>
        </li>
      </ol>
    </div>
  </div>
</template>

<script>
/**
 * Cómo instalar la app: primero pregunta qué teléfono tiene y después muestra los pasos de
 * Android (Chrome) o iPhone (Safari), cada uno con un dibujo de la pantalla y lo que hay que
 * tocar resaltado. Los dibujos son SVG con los colores del sistema (tokens --m-* y --bs-primary).
 * Si Android ofrece el diálogo nativo (`canPrompt`), emite «prompt» para abrirlo.
 */
export default {
  name: "MobileInstallGuide",
  props: {
    name: { type: String, default: "" },
    logo: { type: String, default: "" },
    canPrompt: { type: Boolean, default: false },
  },
  data() {
    return { platform: "" };
  },
  computed: {
    host() {
      return typeof window !== "undefined" ? window.location.hostname : "";
    },
    shortName() {
      const n = String(this.name || "").split(/\s[-–|]\s/)[0];
      return n.length > 12 ? `${n.slice(0, 11)}…` : n;
    },
    steps() {
      if (this.platform === "ios") {
        return [
          { key: "ios-share", text: this.$t("mobile.installIosStep1") },
          { key: "ios-add", text: this.$t("mobile.installIosStep2") },
          { key: "ios-confirm", text: this.$t("mobile.installIosStep3") },
          { key: "open", text: this.$t("mobile.installStepOpen", { name: this.name }) },
        ];
      }
      return [
        { key: "android-menu", text: this.$t("mobile.installAndroidStep1") },
        { key: "android-install", text: this.$t("mobile.installAndroidStep2") },
        { key: "android-confirm", text: this.$t("mobile.installAndroidStep3") },
        { key: "open", text: this.$t("mobile.installStepOpen", { name: this.name }) },
      ];
    },
  },
};
</script>

<style scoped>
.m-guide {
  padding: 0.75rem 1rem 1rem;
  color: var(--m-text);
}
.m-guide__question {
  margin: 0.25rem 0 0.9rem;
  font-size: 1rem;
  font-weight: 600;
  text-align: center;
}
.m-guide__choices {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
}
.m-guide__choice {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4rem;
  padding: 1rem 0.5rem;
  border: 1px solid var(--m-border);
  border-radius: 0.9rem;
  background: var(--m-surface-2);
  color: var(--m-text);
  font-weight: 600;
}
.m-guide__choice i {
  font-size: 2rem;
  color: var(--bs-primary);
}
.m-guide__choice:active {
  background: var(--m-pressed);
}
.m-guide__back {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  margin-bottom: 0.5rem;
  padding: 0.25rem 0;
  border: 0;
  background: transparent;
  color: var(--bs-primary);
  font-size: 0.88rem;
}
.m-guide__quick {
  margin-bottom: 0.75rem;
  text-align: center;
  font-size: 0.9rem;
}
.m-guide__quick p {
  margin: 0 0 0.5rem;
}
.m-guide__quick-btn {
  width: 100%;
  margin: 0;
  border-radius: 999px;
  text-transform: none;
}
.m-guide__or {
  margin-top: 0.75rem !important;
  color: var(--m-muted);
  font-size: 0.82rem;
}
.m-guide__steps {
  list-style: none;
  margin: 0;
  padding: 0;
}
.m-guide__step + .m-guide__step {
  margin-top: 1rem;
}
.m-guide__step-text {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  font-size: 0.92rem;
  line-height: 1.35;
}
.m-guide__num {
  flex-shrink: 0;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  background: var(--bs-primary);
  color: var(--bs-white);
  font-size: 0.8rem;
  font-weight: 600;
  line-height: 1.5rem;
  text-align: center;
}
.m-guide__img {
  display: block;
  width: 100%;
  max-width: 320px;
  margin: 0.5rem auto 0;
}

/* Dibujos: solo tokens del sistema. */
.ig-screen {
  fill: var(--m-surface-2);
  stroke: var(--m-border);
}
.ig-bar {
  fill: var(--m-surface);
}
.ig-shadow {
  stroke: var(--m-border);
}
.ig-line {
  fill: var(--m-border);
}
.ig-text {
  fill: var(--m-text);
  font-size: 9px;
  font-weight: 600;
}
.ig-small {
  fill: var(--m-muted);
  font-size: 8px;
}
.ig-accent {
  fill: var(--bs-primary);
}
.ig-accent-text {
  fill: var(--bs-primary);
  font-size: 8.5px;
  font-weight: 700;
}
.ig-accent-stroke {
  fill: none;
  stroke: var(--bs-primary);
  stroke-width: 1.8;
  stroke-linecap: round;
  stroke-linejoin: round;
}
.ig-muted-stroke {
  fill: none;
  stroke: var(--m-muted);
  stroke-width: 1.8;
  stroke-linecap: round;
  stroke-linejoin: round;
}
.ig-hl-bg {
  fill: rgba(var(--bs-primary-rgb), 0.14);
}
.ig-hl {
  fill: none;
  stroke: var(--bs-primary);
  stroke-width: 2.2;
}
</style>
